import { defineStore } from 'pinia'
import ServiceRepository from '@/repositories/ServiceRepository'
import { DataSnapshot } from 'firebase/database'
import { Filter } from '@/types/Filter'
import DateHelper from '@/helpers/DateHelper'
import { ServiceList } from '@/models/ServiceList'
import { useDriversStore } from '@/services/stores/DriversStore'
import ToastService from '@/services/ToastService'
import i18n from '@/plugins/i18n'
import { Pagination } from '@/types/Pagination'
import Service from '@/models/Service'
import { ServiceCursor } from '@/types/ServiceCursor'
import ServiceHelper from '@/helpers/ServiceHelper'

// Private, non-reactive last-request-wins guard for getHistoryServices.
let historyRequestToken = 0

export const useServicesStore = defineStore('servicesStore', {
  state: () => {
    return {
      pendings: Array<ServiceList>(),
      inProgress: Array<ServiceList>(),
      history: Array<ServiceList>(),
      historyLoading: false,
      currentCursor: <ServiceCursor>{
        id: '',
        created: DateHelper.endOfDayUnix()
      },
      pagination: <Pagination>{
        currentPage: 1,
        perPage: 20,
        totalCount: 0,
        cursor: {
          id: '',
          created: DateHelper.endOfDayUnix()
        }
      },
      canceled: 0,
      completed: 0,
      filter: <Filter>{
        from: DateHelper.stringNow(),
        to: DateHelper.stringNow(),
        clientId: null,
        driverId: null,
        origin: null
      }
    }
  },
  actions: {
    attachDriver(service: ServiceList): ServiceList {
      const { findById } = useDriversStore()
      service.driver = service.driver_id ? findById(service.driver_id) ?? null : null
      return service
    },

    rehydrateServiceDrivers(): void {
      this.pendings.forEach((service) => this.attachDriver(service))
      this.inProgress.forEach((service) => this.attachDriver(service))
      this.history.forEach((service) => this.attachDriver(service))
    },

    async getPendingServices(): Promise<void> {
      const added = (snapshot: DataSnapshot): void => {
        const service = this.setServiceFromDB(snapshot)
        this.pendings.unshift(service)
      }

      const removed = (snapshot: DataSnapshot): void => {
        const service = this.setServiceFromDB(snapshot)
        const index = this.pendings.findIndex(serv => serv.id === service.id)
        this.pendings.splice(index, 1)
      }

      const changed = (snapshot: DataSnapshot): void => {
        const service = this.setServiceFromDB(snapshot)
        const index = this.pendings.findIndex(serv => serv.id === service.id)
        if (index !== -1) {
          this.pendings.splice(index, 1, service)
        } else {
          this.pendings.unshift(service)
        }
      }

      ServiceRepository.pendingListener(added, removed, changed)
    },

    async getInProgressServices(): Promise<void> {
      const { setOccupiedDriver } = useDriversStore()
      const { removeOccupiedDriver } = useDriversStore()
      const added = (snapshot: DataSnapshot): void => {
        const service = this.setServiceFromDB(snapshot)
        this.inProgress.unshift(service)
        if (service.driver_id !== null) {
          setOccupiedDriver(service.driver_id)
        }
      }

      const removed = (snapshot: DataSnapshot): void => {
        const service = this.setServiceFromDB(snapshot)
        const index = this.inProgress.findIndex(serv => serv.id === service.id)
        this.inProgress.splice(index, 1)
        if (service.driver_id) removeOccupiedDriver(service.driver_id)
      }

      ServiceRepository.inProgressListener(added, removed)
    },

    async getHistoryServices(next = true, _contain = false): Promise<void> {
      const from = DateHelper.getFromDate(this.filter.from)
      const to = DateHelper.getToDate(this.filter.to)
      const token = ++historyRequestToken
      this.historyLoading = true

      const options = {
        from: from,
        to: to,
        driverId: this.filter.driverId,
        clientId: this.filter.clientId,
        origin: this.filter.origin,
        perPage: this.pagination.perPage,
        cursor: this.pagination.cursor,
        next: next
      }

      await ServiceRepository.getHistoryPage(options)
        .then((response) => {
          if (token !== historyRequestToken) return
          this.pagination.totalCount = response.totalCount
          this.completed = response.terminatedCount
          this.canceled = response.canceledCount
          this.history.splice(0)
          response.services.forEach((dbService) => {
            const service = this.setServiceFromFS(dbService)
            this.history.push(service)
          })
          this.currentCursor.id = this.history[0]?.id
          this.currentCursor.created = this.history[0]?.created_at
          this.historyLoading = false
        })
        .catch(async (e) => {
          if (token !== historyRequestToken) return
          this.historyLoading = false
          await ToastService.toast(
            ToastService.ERROR,
            i18n.global.t('common.messages.error'),
            e.message
          )
        })
    },

    filterInProgressServices(search: string): Array<ServiceList> {
      const query = search.toLowerCase()
      // Services without an assigned driver are intentionally excluded from search.
      return this.inProgress.filter(service => {
        if (!service.driver) return false
        const plate = ServiceHelper.vehiclePlate(service).toLowerCase()
        const phone = (service.phone ?? '').toLowerCase()
        return plate.includes(query) || phone.includes(query)
      })
    },

    setServiceFromFS(dbService: Service): ServiceList {
      const service = Object.assign(new ServiceList(), dbService)
      service.vehicle = dbService.vehicle ?? null
      return this.attachDriver(service)
    },

    setServiceFromDB(snapshot?: DataSnapshot): ServiceList {
      const service = Object.assign(new ServiceList(), snapshot?.val())
      service.id = snapshot?.key as string
      service.vehicle = snapshot?.val()?.vehicle ?? null
      return this.attachDriver(service)
    },

    resetCursor(): void {
      const defaultCursor = <ServiceCursor>{
        id: '',
        created: DateHelper.endOfDayUnix()
      }
      this.pagination.cursor = defaultCursor
      this.currentCursor = defaultCursor
    }
  }
})
